import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CButton, CCard, CCardBody, CCardHeader, CCol, CModal, CModalBody,
  CModalFooter, CModalHeader, CModalTitle, CRow, CSpinner,
} from '@coreui/react-pro'

import { api } from '../api/client'
import { usePersonLabels, joinLabels } from '../api/people'
import Linkify from '../components/Linkify'
import { fmtDate, fmtTime, fmtMoney } from '../components/format'

interface Flight {
  id: number
  traveler_ids: number[]
  direction: 'arrival' | 'departure'
  date: string
  time_local: string
  airline: string
  flight_number: string
  route: string
  airport: string
  confirmation: string
  notes: string
}

interface Activity {
  id: number
  date: string
  time_local: string | null
  activity: string
  location: string
  who_ids: number[]
  cost_per_person: number | null
  status: 'booked' | 'planning' | 'needs_attention'
  booking_link: string
  notes: string
}

interface Stay {
  id: number
  traveler_ids: number[]
  hotel: string
  town: string
  address: string
  check_in: string
  check_out: string
  status: 'booked' | 'planning'
  confirmation: string
  notes: string
}

type Kind = 'arrival' | 'departure' | 'stay' | 'booked' | 'planning' | 'needs_attention'

interface CalEvent {
  id: string
  kind: Kind
  date: string // ISO yyyy-mm-dd
  time: string | null
  title: string
  flight?: Flight
  activity?: Activity
  stay?: Stay
}

const KIND_STYLE: Record<Kind, { bg: string; fg: string; icon: string; label: string }> = {
  arrival: { bg: '#cfe2ff', fg: '#084298', icon: '🛬', label: 'Arrival' },
  departure: { bg: '#e2e6ea', fg: '#3d4045', icon: '🛫', label: 'Departure' },
  stay: { bg: '#e5d8f5', fg: '#46286b', icon: '🏨', label: 'Hotel stay' },
  booked: { bg: '#d1e7dd', fg: '#0f5132', icon: '🌴', label: 'Booked activity' },
  planning: { bg: '#fff3cd', fg: '#664d03', icon: '💡', label: 'Planning' },
  needs_attention: { bg: '#f8d7da', fg: '#842029', icon: '⚠️', label: 'Needs attention' },
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function iso(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function monthMatrix(anchor: Date): Date[][] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay())
  const weeks: Date[][] = []
  const cur = new Date(start)
  for (let w = 0; w < 6; w++) {
    const days: Date[] = []
    for (let d = 0; d < 7; d++) {
      days.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
    }
    weeks.push(days)
  }
  return weeks
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

const EventChip: React.FC<{ ev: CalEvent; onClick: () => void }> = ({ ev, onClick }) => {
  const st = KIND_STYLE[ev.kind]
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-100 text-start border-0 rounded px-1 mb-1 text-truncate"
      style={{
        fontSize: '0.7rem',
        lineHeight: '1.5',
        background: st.bg,
        color: st.fg,
        padding: '1px 4px',
        cursor: 'pointer',
      }}
      title={ev.title}
    >
      {ev.time ? `${fmtTime(ev.time).replace(' ', '')} ` : ''}{st.icon} {ev.title}
    </button>
  )
}

const Calendar: React.FC = () => {
  const flights = useQuery({
    queryKey: ['flights'],
    queryFn: () => api.get<Flight[]>('/v1/flights'),
  })
  const activities = useQuery({
    queryKey: ['activities'],
    queryFn: () => api.get<Activity[]>('/v1/activities'),
  })
  const stays = useQuery({
    queryKey: ['stays'],
    queryFn: () => api.get<Stay[]>('/v1/stays'),
  })
  const feed = useQuery({
    queryKey: ['calendar-url'],
    queryFn: () => api.get<{ url: string }>('/v1/calendar-url'),
    retry: false,
  })
  const labels = usePersonLabels()

  const events = useMemo<CalEvent[]>(() => {
    const out: CalEvent[] = []
    for (const f of flights.data ?? []) {
      const who = joinLabels(labels, f.traveler_ids) || 'The group'
      out.push({
        id: `f-${f.id}`,
        kind: f.direction,
        date: f.date,
        time: f.time_local,
        title: f.direction === 'arrival' ? `${who} land ${f.airport}` : `${who} fly home`,
        flight: f,
      })
    }
    for (const a of activities.data ?? []) {
      out.push({
        id: `a-${a.id}`,
        kind: a.status,
        date: a.date,
        time: a.time_local,
        title: a.activity,
        activity: a,
      })
    }
    // Booked stays chip on every day from check-in through check-out inclusive.
    for (const s of stays.data ?? []) {
      if (s.status !== 'booked') continue
      const who = joinLabels(labels, s.traveler_ids) || 'The group'
      const place = s.town || s.hotel
      const title = place ? `${who} — ${place}` : who
      for (
        let d = new Date(`${s.check_in}T00:00:00`);
        d <= new Date(`${s.check_out}T00:00:00`);
        d.setDate(d.getDate() + 1)
      ) {
        out.push({ id: `s-${s.id}-${iso(d)}`, kind: 'stay', date: iso(d), time: null, title, stay: s })
      }
    }
    return out.sort((x, y) => (x.date + (x.time ?? '')).localeCompare(y.date + (y.time ?? '')))
  }, [flights.data, activities.data, stays.data, labels])

  const byDay = useMemo(() => {
    const map = new Map<string, CalEvent[]>()
    for (const ev of events) {
      const list = map.get(ev.date) ?? []
      list.push(ev)
      map.set(ev.date, list)
    }
    return map
  }, [events])

  const [anchor, setAnchor] = useState<Date | null>(null)
  const view = useMemo(() => {
    if (anchor) return anchor
    if (events.length) return new Date(`${events[0].date}T00:00:00`)
    return new Date()
  }, [anchor, events])
  const [selected, setSelected] = useState<CalEvent | null>(null)
  const [dayList, setDayList] = useState<{ date: string; events: CalEvent[] } | null>(null)
  const [copied, setCopied] = useState(false)

  const weeks = useMemo(() => monthMatrix(view), [view])
  const today = iso(new Date())

  const stepMonth = (delta: number) =>
    setAnchor(new Date(view.getFullYear(), view.getMonth() + delta, 1))

  const copyFeed = async () => {
    const url = feed.data?.url
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copy this URL:', url)
    }
  }

  if (flights.isLoading || activities.isLoading || stays.isLoading) {
    return <div className="text-center py-5"><CSpinner /></div>
  }

  const monthTitle = view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <>
      <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0 me-auto">📅 {monthTitle}</h3>
        <CButton color="secondary" variant="outline" size="sm" onClick={() => stepMonth(-1)}>‹ Prev</CButton>
        <CButton color="secondary" variant="outline" size="sm" onClick={() => setAnchor(null)}>Reset</CButton>
        <CButton color="secondary" variant="outline" size="sm" onClick={() => stepMonth(1)}>Next ›</CButton>
        {feed.data?.url && (
          <CButton color="primary" size="sm" onClick={copyFeed}>
            {copied ? '✓ Feed copied' : 'Copy subscribe link'}
          </CButton>
        )}
      </div>

      <CCard>
        <CCardHeader>
          <div className="d-flex flex-wrap gap-3 small">
            {(Object.keys(KIND_STYLE) as Kind[]).map((k) => (
              <span key={k} className="d-inline-flex align-items-center gap-1">
                <span
                  className="d-inline-block rounded"
                  style={{ width: 12, height: 12, background: KIND_STYLE[k].bg, border: `1px solid ${KIND_STYLE[k].fg}` }}
                />
                {KIND_STYLE[k].icon} {KIND_STYLE[k].label}
              </span>
            ))}
          </div>
        </CCardHeader>
        <CCardBody className="p-2 p-md-3">
          <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center small text-uppercase text-body-secondary fw-semibold pb-1">
                <span className="d-none d-sm-inline">{d}</span>
                <span className="d-sm-none">{d[0]}</span>
              </div>
            ))}
            {weeks.flat().map((day) => {
              const key = iso(day)
              const dayEvents = byDay.get(key) ?? []
              const inMonth = sameMonth(day, view)
              const isToday = key === today
              const isWeekend = day.getDay() === 0 || day.getDay() === 6
              return (
                <div
                  key={key}
                  className={[
                    'rounded border p-1',
                    'd-flex flex-column',
                    inMonth ? '' : 'opacity-50',
                    isWeekend && inMonth ? 'bg-body-tertiary' : '',
                  ].join(' ')}
                  style={{
                    minHeight: 92,
                    minWidth: 0, // let 1fr columns shrink so chips truncate instead of stretching the grid
                    borderColor: isToday ? 'var(--cui-primary)' : undefined,
                    borderWidth: isToday ? 2 : 1,
                  }}
                >
                  <div className={`text-end small ${isToday ? 'fw-bold text-primary' : 'text-body-secondary'}`}>
                    {day.getDate()}
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <EventChip key={ev.id} ev={ev} onClick={() => setSelected(ev)} />
                    ))}
                    {dayEvents.length > 3 && (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 x-1 small text-body-secondary"
                        style={{ fontSize: '0.7rem', cursor: 'pointer' }}
                        onClick={() => setDayList({ date: key, events: dayEvents })}
                      >
                        +{dayEvents.length - 3} more
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CCardBody>
      </CCard>

      {/* Event detail modal */}
      <CModal visible={!!selected} onClose={() => setSelected(null)}>
        <CModalHeader>
          <CModalTitle>
            {selected ? `${KIND_STYLE[selected.kind].icon} ${selected.title}` : ''}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selected?.flight && (
            <>
              <CRow className="mb-1">
                <CCol xs={5} className="text-body-secondary">When</CCol>
                <CCol>{fmtDate(selected.flight.date)} · {fmtTime(selected.flight.time_local)}</CCol>
              </CRow>
              <CRow className="mb-1">
                <CCol xs={5} className="text-body-secondary">Flight</CCol>
                <CCol>{[selected.flight.airline, selected.flight.flight_number].filter(Boolean).join(' ') || '—'}</CCol>
              </CRow>
              <CRow className="mb-1">
                <CCol xs={5} className="text-body-secondary">Route</CCol>
                <CCol>{selected.flight.route || selected.flight.airport}</CCol>
              </CRow>
              {selected.flight.confirmation && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Confirmation</CCol>
                  <CCol className="font-monospace">{selected.flight.confirmation}</CCol>
                </CRow>
              )}
              {selected.flight.notes && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Notes</CCol>
                  <CCol>{selected.flight.notes}</CCol>
                </CRow>
              )}
            </>
          )}
          {selected?.activity && (
            <>
              <CRow className="mb-1">
                <CCol xs={5} className="text-body-secondary">When</CCol>
                <CCol>{fmtDate(selected.activity.date)}{selected.activity.time_local ? ` · ${fmtTime(selected.activity.time_local)}` : ''}</CCol>
              </CRow>
              {selected.activity.location && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Where</CCol>
                  <CCol>{selected.activity.location}</CCol>
                </CRow>
              )}
              {selected.activity.who_ids?.length > 0 && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Who</CCol>
                  <CCol>{joinLabels(labels, selected.activity.who_ids)}</CCol>
                </CRow>
              )}
              {selected.activity.cost_per_person != null && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Cost</CCol>
                  <CCol>{fmtMoney(selected.activity.cost_per_person)} / person</CCol>
                </CRow>
              )}
              {selected.activity.booking_link && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Booking</CCol>
                  <CCol>
                    <a href={selected.activity.booking_link} target="_blank" rel="noopener noreferrer">open link</a>
                  </CCol>
                </CRow>
              )}
              {selected.activity.notes && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Notes</CCol>
                  <CCol>{selected.activity.notes}</CCol>
                </CRow>
              )}
            </>
          )}
          {selected?.stay && (
            <>
              <CRow className="mb-1">
                <CCol xs={5} className="text-body-secondary">Dates</CCol>
                <CCol>{fmtDate(selected.stay.check_in)} → {fmtDate(selected.stay.check_out)}</CCol>
              </CRow>
              {selected.stay.hotel && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Hotel / rental</CCol>
                  <CCol>{selected.stay.hotel}</CCol>
                </CRow>
              )}
              {selected.stay.address && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Address</CCol>
                  <CCol><Linkify text={selected.stay.address} /></CCol>
                </CRow>
              )}
              {selected.stay.traveler_ids?.length > 0 && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Who</CCol>
                  <CCol>{joinLabels(labels, selected.stay.traveler_ids)}</CCol>
                </CRow>
              )}
              {selected.stay.confirmation && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Confirmation</CCol>
                  <CCol className="font-monospace">{selected.stay.confirmation}</CCol>
                </CRow>
              )}
              {selected.stay.notes && (
                <CRow className="mb-1">
                  <CCol xs={5} className="text-body-secondary">Notes</CCol>
                  <CCol><Linkify text={selected.stay.notes} /></CCol>
                </CRow>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setSelected(null)}>Close</CButton>
        </CModalFooter>
      </CModal>

      {/* Busy-day modal */}
      <CModal visible={!!dayList} onClose={() => setDayList(null)}>
        <CModalHeader>
          <CModalTitle>{dayList ? fmtDate(dayList.date) : ''}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {dayList?.events.map((ev) => (
            <div key={ev.id} className="mb-2">
              <EventChip ev={ev} onClick={() => { setDayList(null); setSelected(ev) }} />
            </div>
          ))}
        </CModalBody>
      </CModal>
    </>
  )
}

export default Calendar
